/**
 * Default demo quotes for KdsRoundTable.
 *
 * Six quotes in the methodological register of the Korum lexicon.
 * These are intentionally in English and are not subject to i18n (design decision D2).
 *
 * Consumers can override them by passing the `quotes` prop to `KdsRoundTable`.
 */
export const DEFAULT_QUOTES: string[] = [
  'The onboarding drop-off at step 3 is a structural issue, not a copy problem.',
  "Pricing isn't the friction — it's the lack of a trial that removes the risk.",
  "Three of six personas hesitated at the same decision point. That's a signal.",
  "The protocol surfaces disagreement early. That's the point.",
  'Segment 4 has a fundamentally different mental model of what this tool does.',
  "We're conflating activation with retention. They're solved differently."
];

/**
 * 20 quotes for the KdsQuoteFeed animated debate feed.
 *
 * All quotes are in English (design decision D2) and cover diverging perspectives
 * on a fictional pricing panel. Used by `useQuoteFeed` for the random draw algorithm.
 * Not subject to i18n.
 */
export const PRICING_DEBATE_QUOTES: readonly string[] = [
  "The price increase doesn't address the core friction — it just moves the churn point earlier.",
  'Segment 3 is price-anchored to the trial. Removing it resets their entire mental model of value.',
  'A 40% uplift without a feature unlock is a positioning error, not a pricing decision.',
  "The protocol surfaces what surveys miss: three of six personas are not resistant to the price, they're resistant to the justification.",
  "Willingness to pay diverges sharply at the team tier. The data doesn't support a uniform increase.",
  "This isn't about affordability — it's about perceived fairness. The panel splits on that distinction.",
  'Segment 1 treats the current price as a quality signal. Raising it might actually reduce churn in that cohort.',
  "The friction is at the moment of renewal, not at sign-up. The pricing change amplifies an existing weak point.",
  "Four personas mentioned the competitor's free tier unprompted. Price sensitivity is contextual, not absolute.",
  "Increase the price only after you've solved the activation gap — right now you're charging more for an incomplete experience.",
  "The panel doesn't object to the number. It objects to the absence of a credible before/after narrative.",
  'Segment 2 has a fundamentally different ROI model. For them, the new price is justified — for Segment 4, it\'s a blocker.',
  'Annual billing mitigates the perceived increase. Monthly pricing at the new rate will spike cancellations.',
  'Three personas cited onboarding as the primary value driver. Fix the drop-off first, then reprice.',
  "The debate surfaces a structural issue: the feature set doesn't yet match the premium positioning.",
  "Seat-based pricing aligns with the team's actual usage pattern. Per-workspace pricing doesn't.",
  "This persona cohort ran the numbers. The ROI holds — but only if the panel size stays above four.",
  'Price anchoring to the old rate is strong in Segment 3. The increase needs a reframe, not just a number.',
  "The protocol identifies the real objection: it's not the price, it's the lack of a downgrade path.",
  'Two cohorts are in direct contradiction on perceived value. A single price point cannot satisfy both.'
] as const;
